<template>
  <client-only>
    <div class="min-h-screen bg-black4 font-Spectral md:mt-[-50px] md:pt-[50px] lg:mt-[-70px] lg:pt-[70px]">
      <transition name="fade2">
        <div v-if="hasSucceeded">
          <OrganismsPagesEnStoriesMainVisualSection :en-story="enStory" />
          <OrganismsPagesEnStoriesArticleSection :en-story="enStory" :featured-experiences="storyFeaturedExperiences" />
          <footer class="bg-black4">
            <div class="footer__inner m-auto px-4 py-14 md:py-20 md:pl-[5%] md:pr-0">
              <OrganismsPagesEnStoriesRelatedStoriesSection v-if="enRecommendedStories.length > 0" class="mb-6 md:mb-12" :en-stories="enRecommendedStories" />
              <ClientOnly>
                <RecommendedExperiencesSection :experience-sections="experienceSections" :theme-list="themeList" />
              </ClientOnly>
            </div>
          </footer>
        </div>
        <div v-else-if="isLoading" />
      </transition>
    </div>
  </client-only>
</template>

<script setup lang="ts">
import { useEnStories } from '@/composables/models/useEnStories';
import { useEnStory } from '@/composables/models/useEnStory';
import { useMicroCMSDetailParams } from '@/composables/utils/useMicroCMSDetailParams';
import { useErrorCreator } from '@/composables/utils/wabunka/useErrorCreator';
import { buildMetaData } from '@/models/enStory';
import type { WabunkaExperienceListViewModel } from '@/models/wabunka/wabunkaExperience';
import { useLastModifiedHeader } from '@/wabunka-src/common/composables/general/useLastModifiedHeader/useLastModifiedHeader';
import RecommendedExperiencesSection from '@/wabunka-src/common/features/Experience/components/RecommendedExperiences/RecommendedExperiencesSection.vue';
import { sendGAWabunkaStoryViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/storyView';
import { STORIES_COUNT_FOR_RECOMMEND } from '@/wabunka-src/constants/enStories';
import { EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS } from '@/wabunka-src/constants/experienceSectionWebPagePath';
import { useGetExperienceSectionsQuery } from '@/wabunka-src/features/Experience';
import { useFeaturedListQuery } from '@/wabunka-src/features/Featured';
import { useAsyncWabunkaExperiencesSearch } from '#composables/models/wabunka/useAsyncWabunkaExperiencesSearch';
const { experienceSections } = await useGetExperienceSectionsQuery(EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS.STORY_DETAIL, []);

const { themeList } = await useFeaturedListQuery({ types: ['theme'] });

const route = useRoute();

const { hasDraftKey } = useMicroCMSDetailParams();
const { enStory, hasError, hasSucceeded, isLoading } = await useEnStory();
useLastModifiedHeader(enStory.value.revisedAt);
const slugs = ref<string[]>(enStory.value.enExperienceIds?.map((item) => item.contentId) ?? []);
const storyFeaturedExperiences = ref<WabunkaExperienceListViewModel>([]);
if (slugs.value.length > 0) {
  const { experiences } = await useAsyncWabunkaExperiencesSearch({
    experienceEventStartAtDates: ref(''),
    destinations: ref(''),
    themes: ref(''),
    slugs: ref(slugs.value),
  });
  storyFeaturedExperiences.value = experiences.value;
}

const limit = ref<number>(STORIES_COUNT_FOR_RECOMMEND);
const storyId = Array.isArray(route.params.id) ? (route.params.id[0] ?? '') : route.params.id;
const { enRecommendedStories } = await useEnStories({ limit }, { id: storyId });
const { createWabunkaError } = useErrorCreator();

if (hasError.value) {
  throw createWabunkaError({
    statusCode: 404,
    fatal: true,
  });
}

const isNoIndex = enStory.value.isPrivate || hasDraftKey.value || !hasSucceeded;

const { title, description, robots, link } = buildMetaData(enStory.value, isNoIndex, route.path);

useServerSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
});

if (robots) useRobotsRule(robots);

useHead({ link });
onMounted(() => {
  // GAイベント送信
  sendGAWabunkaStoryViewEvent(storyId);
});
</script>
